import React, { FunctionComponent, useMemo } from 'react'
import { FormattedDateTimeRange, FormattedMessage } from 'react-intl'
import { Animated, Platform, ViewStyle } from 'react-native'

import { addMinutes } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { GearIcon } from '../../atoms/icons/GearIcon'
import { RecordingBadge } from '../../atoms/recordingBadge/RecordingBadge'
import TertiaryIconButton from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Title } from '../../atoms/title/Title'
import { useZoom } from '../../hooks/useZoom/useZoom'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'

const Container = styled(Animated.View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
})

const SessionDetailsContainer = styled.View(({ theme }) => ({
  gap: theme.spacing['4px'],
}))

const OuterContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['8px'],
  top: theme.spacing['16px'],
  left: theme.spacing['24px'],
  right: theme.spacing['24px'],
  position: 'absolute',
  zIndex: 2,
}))

export type ZoomMobileHeaderProps = {
  participantName: string
  sessionStartDate: Date
  sessionDuration: number
  sessionNumber?: number
  openSettings?: () => void
  style?: ViewStyle
  hidden: boolean
  hideSessionNumber?: boolean
  hideNotRecordingBadge?: boolean
}

export const ZoomMobileHeader: FunctionComponent<ZoomMobileHeaderProps> = ({
  participantName,
  sessionStartDate,
  sessionDuration,
  sessionNumber,
  openSettings,
  hidden,
  style,
  hideSessionNumber = false,
  hideNotRecordingBadge = false,
}) => {
  const { isRecording, sessionStarted } = useZoom()
  const theme = useTheme() as ThemeType

  const sessionEndDate = useMemo(
    () => addMinutes(sessionStartDate, sessionDuration),
    [sessionStartDate, sessionDuration],
  )
  return (
    <OuterContainer>
      {sessionStarted && (!hideNotRecordingBadge || isRecording) && (
        <RecordingBadge isRecording={isRecording ?? false} />
      )}
      <Container style={style} pointerEvents={hidden ? 'none' : 'auto'}>
        <SessionDetailsContainer>
          <Title
            size={TitleSize.SMALL}
            text={
              hideSessionNumber ? (
                participantName
              ) : (
                <FormattedMessage
                  defaultMessage='{participantName} • Session {sessionNumber}'
                  description='Title of video session with participant name and session number'
                  values={{ participantName, sessionNumber }}
                />
              )
            }
          />
          <BodyText
            size={BodyTextSize.SMALL}
            text={
              <FormattedDateTimeRange
                from={sessionStartDate}
                to={sessionEndDate}
                hour='numeric'
                minute='numeric'
                timeZoneName='short'
              />
            }
          />
        </SessionDetailsContainer>
        {Platform.OS === 'web' && (
          <TertiaryIconButton
            leftIcon={<GearIcon color={theme.colors.iconDefault} />}
            onPress={() => openSettings?.()}
          />
        )}
      </Container>
    </OuterContainer>
  )
}
